import { type ReactNode } from "react";
import { useAdminT } from "../../../hooks/useAdminT";

// ─── StatusBadge ──────────────────────────────────────────────────────────────

interface BadgeProps {
  active: boolean;
  label?: string;
}

export function StatusBadge({ active, label }: BadgeProps) {
  const t = useAdminT();
  const defaultLabel = active ? t("tabifotoAdmin.common.active") : t("tabifotoAdmin.common.inactive");
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-light ${
        active ? "bg-emerald-50 text-emerald-600" : "bg-[#D9D9D9]/50 text-[#2C2C2C]/40"
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-[#2C2C2C]/30"}`} />
      {label ?? defaultLabel}
    </span>
  );
}

// ─── PublishBadge ─────────────────────────────────────────────────────────────

export function PublishBadge({ published }: { published: boolean }) {
  const t = useAdminT();
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-light ${
        published ? "bg-emerald-50 text-emerald-600" : "bg-[#D9D9D9]/50 text-[#2C2C2C]/40"
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${published ? "bg-emerald-500" : "bg-[#2C2C2C]/30"}`} />
      {published ? t("tabifotoAdmin.common.published") : t("tabifotoAdmin.common.draft")}
    </span>
  );
}

// ─── LangTabs ─────────────────────────────────────────────────────────────────

interface LangTabsProps {
  activeLang: "zh" | "en" | "ja";
  onChange: (lang: "zh" | "en" | "ja") => void;
}

export function LangTabs({ activeLang, onChange }: LangTabsProps) {
  const t = useAdminT();
  const langs: Array<{ code: "zh" | "en" | "ja"; label: string }> = [
    { code: "zh", label: t("tabifotoAdmin.lang.zh") },
    { code: "en", label: t("tabifotoAdmin.lang.en") },
    { code: "ja", label: t("tabifotoAdmin.lang.ja") },
  ];
  return (
    <div className="flex gap-1 bg-[#F7F6F3] p-1 rounded-lg w-fit">
      {langs.map((l) => (
        <button
          key={l.code}
          onClick={() => onChange(l.code)}
          className={`px-3 py-1 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${
            activeLang === l.code
              ? "bg-white text-[#2C2C2C]"
              : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

// ─── SortOrderInput ───────────────────────────────────────────────────────────

export function SortOrderInput({ order, onChange }: { order: number; onChange: (v: number) => void }) {
  return (
    <input
      type="number"
      min={1}
      value={order}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-16 px-2 py-1 rounded-md border border-[#D9D9D9] text-xs text-[#2C2C2C] bg-white focus:outline-none focus:border-[#C8B8A6] text-center"
    />
  );
}

// ─── Toggle ───────────────────────────────────────────────────────────────────

export function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${
        checked ? "bg-[#C8B8A6]" : "bg-[#D9D9D9]"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
          checked ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </button>
  );
}

// ─── AdminTable ───────────────────────────────────────────────────────────────

interface TableProps {
  headers: string[];
  children: ReactNode;
  empty?: boolean;
  emptyMessage?: string;
}

export function AdminTable({ headers, children, empty, emptyMessage }: TableProps) {
  const t = useAdminT();
  return (
    <div className="bg-white rounded-lg border border-[#D9D9D9]/60 overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#D9D9D9]/60 bg-[#F7F6F3]">
            {headers.map((h) => (
              <th key={h} className="text-left px-5 py-3 text-xs uppercase tracking-wider text-[#2C2C2C]/40 font-light">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {empty ? (
            <tr>
              <td colSpan={headers.length} className="text-center py-12 text-[#2C2C2C]/30 text-sm font-light">
                {emptyMessage ?? t("tabifotoAdmin.common.empty")}
              </td>
            </tr>
          ) : (
            children
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── AdminModal ───────────────────────────────────────────────────────────────

interface ModalProps {
  title: string;
  onClose: () => void;
  onSave: () => void;
  saving?: boolean;
  children: ReactNode;
}

export function AdminModal({ title, onClose, onSave, saving, children }: ModalProps) {
  const t = useAdminT();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#D9D9D9]/60">
          <h2 className="text-base font-light text-[#2C2C2C]">{title}</h2>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center text-[#2C2C2C]/40 hover:text-[#2C2C2C] cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#D9D9D9]/60">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2 rounded-md text-sm border border-[#D9D9D9] text-[#2C2C2C]/60 hover:border-[#2C2C2C]/40 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40"
          >
            {t("tabifotoAdmin.common.cancel")}
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="px-5 py-2 rounded-md text-sm bg-[#2C2C2C] text-white hover:bg-[#2C2C2C]/80 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center gap-2"
          >
            {saving && (
              <span className="w-3.5 h-3.5 border border-white/40 border-t-white rounded-full animate-spin" />
            )}
            {t("tabifotoAdmin.common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ConfirmDialog ────────────────────────────────────────────────────────────

interface ConfirmProps {
  title: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({ title, message, confirmLabel, danger, onConfirm, onCancel }: ConfirmProps) {
  const t = useAdminT();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-lg p-6 max-w-sm w-full">
        <h3 className="text-base font-light text-[#2C2C2C] mb-2">{title}</h3>
        <p className="text-sm text-[#2C2C2C]/50 mb-5">{message}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} className="px-4 py-2 rounded-md text-sm border border-[#D9D9D9] text-[#2C2C2C]/60 cursor-pointer whitespace-nowrap">
            {t("tabifotoAdmin.common.cancel")}
          </button>
          <button onClick={onConfirm}
            className={`px-4 py-2 rounded-md text-sm text-white cursor-pointer whitespace-nowrap ${
              danger ? "bg-red-500 hover:bg-red-600" : "bg-[#2C2C2C] hover:bg-[#2C2C2C]/80"
            }`}>
            {confirmLabel ?? t("tabifotoAdmin.common.delete")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── LoadingState ─────────────────────────────────────────────────────────────

export function LoadingState() {
  const t = useAdminT();
  return (
    <div className="bg-white rounded-lg border border-[#D9D9D9]/60 py-16 flex items-center justify-center gap-3 text-[#2C2C2C]/30 text-sm font-light">
      <span className="w-4 h-4 border border-[#C8B8A6]/40 border-t-[#C8B8A6] rounded-full animate-spin" />
      {t("tabifotoAdmin.common.loading")}
    </div>
  );
}

// ─── ErrorState ───────────────────────────────────────────────────────────────

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const t = useAdminT();
  return (
    <div className="bg-white rounded-lg border border-red-100 py-12 flex flex-col items-center gap-3 text-center px-6">
      <div className="w-8 h-8 flex items-center justify-center text-red-300">
        <i className="ri-error-warning-line text-xl" />
      </div>
      <p className="text-sm text-[#2C2C2C]/50 font-light">{message || t("tabifotoAdmin.common.error_load")}</p>
      {onRetry && (
        <button onClick={onRetry}
          className="flex items-center gap-1.5 text-xs text-[#C8B8A6] hover:text-[#2C2C2C] cursor-pointer whitespace-nowrap">
          <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-refresh-line" /></div>
          {t("tabifotoAdmin.common.retry")}
        </button>
      )}
    </div>
  );
}

// ─── ApiModeBanner ────────────────────────────────────────────────────────────

export function ApiModeBanner({ isApiMode }: { isApiMode: boolean }) {
  const t = useAdminT();
  if (isApiMode) return null;
  return (
    <div className="mb-4 flex items-center gap-2 px-4 py-2.5 bg-[#C8B8A6]/10 border border-[#C8B8A6]/30 rounded-lg">
      <div className="w-4 h-4 flex items-center justify-center text-[#C8B8A6] shrink-0">
        <i className="ri-information-line" />
      </div>
      <p className="text-xs text-[#2C2C2C]/50 font-light">{t("tabifotoAdmin.common.api_not_configured")}</p>
    </div>
  );
}

// ─── FormField ────────────────────────────────────────────────────────────────

interface FieldProps {
  label: string;
  required?: boolean;
  children: ReactNode;
}

export function FormField({ label, required, children }: FieldProps) {
  return (
    <div className="mb-4">
      <label className="block text-xs text-[#2C2C2C]/60 font-light mb-1.5 uppercase tracking-wider">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

export const inputCls =
  "w-full px-3 py-2 rounded-md border border-[#D9D9D9] text-sm text-[#2C2C2C] bg-white focus:outline-none focus:border-[#C8B8A6] font-light";
